/*eslint-disable*/
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

// components
import { Box } from '@mui/material'
import UserForm from '../../components/userForm'
import ConfirmationDialog from '../../components/confirmationDialog'
import LoadingSpinner from '../../components/loadingSpinner'

// hooks
import { useParams } from 'react-router-dom'
import useUser from '../../hooks/useUser'
import useDialogVisibility from '../../hooks/useDialogVisibility'

// constants
import { EDIT_FORM_FIELDS_CONFIG } from './editUser.constants'
import { TENANT_CUSTOM_ATTRIBUTE_SCHEMA } from '../../utils/config'

// helpers
import {
  getInitialUser,
  makeEditUserDTO,
  areFormValuesChanged,
  getSelectedGroupsCount,
} from './editUser.helpers'
import { removeEmptyProperties } from '../../helpers/general'
import { useIsMobileView } from '../../utils/isMobileView'
import { isSuperAdmin } from '../../helpers/user'

//services
import { updateUser } from '../../services/user'
import { validateUserSubmit, hasJnjGroupAccess } from '../../components/userForm/helpers/userForm.validators'
import Breadcrumbs from '../../components/Breadcrumbs'

//css
import styles from './EditUser.module.scss'
import InformationIcon from '../../assets/info-icon.svg'

// regex that prevents its.jnj.com email but accepts all others  
const EDIT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{1,}$/

function EditUser() {
  const superAdmin = isSuperAdmin()
  const jnjGroupAccess = hasJnjGroupAccess()
  const { userId } = useParams()
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()
  const { user: userObj, isUserLoading } = useUser(userId)
  const navigate = useNavigate()
  const [user, setUser] = useState({})
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const selectedTenant = localStorage.getItem('tenant')
  const attributeSchema = JSON.parse(TENANT_CUSTOM_ATTRIBUTE_SCHEMA || '{}')
  const customAttributes = attributeSchema[selectedTenant] || []
  const [
    isCancelConfirmationDialogOpen,
    openCancelConfirmationDialog,
    closeCancelConfirmationDialog,
  ] = useDialogVisibility(false)

  const breadcrumbPath = [
    {label: 'users-edit-tab', path: '/search-user'},
    { label: `${user.firstName} ${user.lastName ? user.lastName : ''}`, path: `/view-user/${userId}` },
    {label: 'edit-user'}
  ]

  useEffect(() => {
    if (userObj) {
      const initialUser = getInitialUser(userObj, customAttributes)
      setUser(initialUser)
    }
  }, [userObj])

  const handleUpdateUserSuccess = () => {
    setErrors({})
    toast.success(t('user-details-updated-success'))
    navigate(`/view-user/${userObj.id}`)
  }

  const handleUpdateUserFailure = (error) => {
    toast.error(error?.message)
  }

  const setAsSubmitted = () => setIsSubmitting(false)

  const onSubmit = () => {
    const errorMsgs = validateUserSubmit(user, EDIT_EMAIL_REGEX, (jnjGroupAccess || superAdmin), userObj)
    if (Object.keys(removeEmptyProperties(errorMsgs)).length >= 1) {
      setErrors(errorMsgs)
      return
    }
    setIsSubmitting(true)
    const userDTO = makeEditUserDTO((jnjGroupAccess || superAdmin), userObj, user, customAttributes)
    updateUser(userDTO, userObj?.id)
      .then(handleUpdateUserSuccess)
      .catch(handleUpdateUserFailure)
      .finally(setAsSubmitted)
  }

  const onCancel = () => {
    openCancelConfirmationDialog()
  }

  const handleSubmitCancelConfirmationDialog = () => {
    const initialUser = getInitialUser(userObj, customAttributes)
    setUser(initialUser)
    setErrors({})
    closeCancelConfirmationDialog()
    navigate(`/view-user/${userId}`)
  }

  const areUserValuesChanged = areFormValuesChanged(userObj, user, customAttributes)
  if (isUserLoading) {
    return <LoadingSpinner />
  }

  return (
    <Box sx={{ width: '100vw' }}>
      <div
        style={{ backgroundColor: '#F5F5F5', width: '100vw', padding: '20px' }}
      >
        <Breadcrumbs
          crumbs={breadcrumbPath}
        />
        <div className={styles[isMobileView ? 'edit-user-name-title-mobile' : 'edit-user-name-title']}>
          {t('edit-user')}: {user.firstName} {user.lastName ? user.lastName : ''}
        </div>
      </div>
      <div className={styles[isMobileView ? 'user-tips-container-mobile' : 'user-tips-container']}>
        <div className={styles['user-tips-content']}>
          <div className={styles['user-tips-locked']}>
            <img src={InformationIcon} alt="" style={{ marginBottom: '4px' }} />
            <span>{t('edit-user-required-info')}</span>
          </div>
        </div>
      </div>
      <UserForm
        user={user}
        setUser={setUser}
        errors={errors}
        setErrors={setErrors}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        formFieldsConfig={EDIT_FORM_FIELDS_CONFIG}
        primaryButtonLabel={t('edit-user-update-button')}
        areFormValuesChanged={areUserValuesChanged}
        getSelectedGroupsCount={getSelectedGroupsCount(userObj, customAttributes)}
        emailRegex={EDIT_EMAIL_REGEX}
        isCreateUser={false}
        existingUser={userObj}
      />
      <ConfirmationDialog
        showDialog={isCancelConfirmationDialogOpen}
        onSubmit={handleSubmitCancelConfirmationDialog}
        onCancel={closeCancelConfirmationDialog}
      />
    </Box>
  )
}

export default EditUser
